import { LOCALES } from "common/constants";
import { getSchemaMarkup } from "common/FunctionUtils";
import { blobURL } from "common/StringUtils";
import Footer from "components/layout/Footer";
import Header from "components/layout/Header";
import OuterCalculator from "components/OuterCalculator";
import React, { useEffect } from "react";
import { Button, Container, Table } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

export const InvoiceWithoutCompany = ({ dashboardFooter }) => {
  const schemaMarkup = getSchemaMarkup("fakturera-utan-foretag-eller-bolag");
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);

  let language = localStorage.getItem("i18nextLng");
  const invoiceWithoutCompanyBanner = `${blobURL}/invoice_without_company.png`;

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  return (
    <div>
      <Helmet>
        {schemaMarkup && (
          <script type="application/ld+json">
            {JSON.stringify(schemaMarkup)}
          </script>
        )}
        <title>
          {t(
            "Invoice without a Company or Business as a Freelancer with Invozio"
          )}
        </title>
        <meta
          name="description"
          content={t(
            "Invoice without a company or business easily! With Invozio, you can invoice safely and efficiently without your own business as a freelancer. Avoid administration and get paid directly."
          )}
        />
        {language === LOCALES.EN_US ? (
          <link
            rel="canonical"
            href="https://www.invozio.com//invoice-without-company-or-business"
          />
        ) : (
          <link
            rel="canonical"
            href="https://www.invozio.com//fakturera-utan-foretag-eller-bolag"
          />
        )}
      </Helmet>

      <Header />
      <Container className="p-0 self_new negotiate neg_rspn">
        <div className="guidance">
          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center title">
                <div className="col-md-6">
                  <div className="page-heading">
                    <h2 className="purple">{t("Invoice without a company")}</h2>

                    {!user && (
                      <Button
                        className="mt-2 create-account filled home_create_account"
                        onClick={() => {
                          navigate(
                            language === LOCALES.EN_US
                              ? `/emailregister?lang=${LOCALES.EN_US}`
                              : `/e-postregister?lang=${LOCALES.SV_SE}`
                          );
                          sessionStorage.setItem("accountType", 1);
                        }}
                      >
                        {t("Create Free Account")}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="self-imgs text-right">
                    <img
                      src={invoiceWithoutCompanyBanner}
                      alt={t(
                        "invoice without company, invoice without business"
                      )}
                      loading="lazy"
                      className="img-fluid"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h1 className="purple">
                    {t(
                      "Invoice Without a Company – Everything You Need to Know"
                    )}
                  </h1>
                  <p>
                    {t(
                      "Invoicing without the need to start your own company has become increasingly popular among freelancers, consultants, and gig workers in Sweden. By using self-employment companies, such as Invozio, you can send invoices and receive your salary without managing the administrative burdens of running your own business. In this article, we'll dive deeper into what self-employment entails, the benefits of invoicing without a company, and how Invozio works. You'll also get practical steps to get started."
                    )}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h2 className="purple">
                    {t("What Does It Mean to Invoice Without a Company?")}
                  </h2>
                  <p>
                    {t(
                      "Invoicing without a company means that, as a private individual, you can invoice for your services or assignments without the need to register your own business. This is done by becoming"
                    )}
                    <Link
                      style={{
                        fontWeight: "600",
                        textDecoration: "underline",
                      }}
                      to={
                        language === LOCALES.EN_US
                          ? `/self-employment?lang=${LOCALES.EN_US}`
                          : `/egenanstallning?lang=${LOCALES.SV_SE}`
                      }
                    >
                      {" "}
                      <u>{t("self-employed ")}</u>
                    </Link>{" "}
                    {t(
                      "with a self-employment company like Invozio. Invozio takes care of all the administration. From bookkeeping and VAT to taxes and employer contributions, Invozio handles everything, allowing you to focus on your core business."
                    )}
                  </p>
                  <blockquote className="blockquote_txt">
                    {t(
                      "“We have thousands of consultants and freelancers across all industries for whom it is not profitable to start their own business. It's easier to register and let us handle all the administration,” says Didrik Selder, CEO at Invozio."
                    )}
                  </blockquote>
                </div>
              </div>
            </div>
          </section>

          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h3 className="purple">
                    {t("How Self-Employment Works in Practice")}
                  </h3>
                  <p>
                    {t("Self-employment means you register with a")}
                    <Link
                      style={{
                        fontWeight: "600",
                        textDecoration: "underline",
                      }}
                      to={
                        language === LOCALES.EN_US
                          ? `/self-employment?lang=${LOCALES.EN_US}`
                          : `/egenanstallning?lang=${LOCALES.SV_SE}`
                      }
                    >
                      {" "}
                      <u>{t("self-employment company")}</u>
                    </Link>{" "}
                    {t(
                      "like Invozio, where you sign up as an employee and let us manage all the administrative aspects of the projects that consultants and freelancers carry out. You can then easily take on assignments and create invoices that are sent to your clients via the platform. When the client pays the invoice, Invozio handles taxes and social security contributions before your salary is paid out. It's a convenient way to work independently without having to start your own business."
                    )}
                  </p>
                </div>
              </div>
            </div>
          </section>
          <section className="guide-section">
            <div className="container">
              <div className="purple row align-items-center">
                <div className="guide">
                  <h2 className="purple">
                    {t("Benefits of Invoicing Without a Company")}
                  </h2>
                  <p>
                    {t(
                      "Invoicing without a company offers several advantages, especially for those who want to work flexibly and focus on their profession without dealing with administrative tasks."
                    )}
                  </p>
                </div>
                 <div style={{ display: "block", width: "100%" }}>
                  <h3 className="purple">{t("Simplicity")}</h3>
                  <p>
                    {t(
                      "You avoid handling bookkeeping, accounting, and other administrative duties that come with running your own business. Invozio takes care of everything."
                    )}
                  </p>
                </div>

                 <div style={{ display: "block", width: "100%" }}>
                  <h3 className="purple">{t("Quick Start")}</h3>
                  <p>
                    {t(
                      "Since no company registration is required, you can start taking on assignments and invoicing immediately."
                    )}
                  </p>
                </div>

                 <div style={{ display: "block", width: "100%" }}>
                  <h3 className="purple">{t("Flexibility")}</h3>
                  <p>
                    {t(
                      "You have the freedom to choose your assignments and set your own working hours, whether you want to work full-time, part-time, or seasonally."
                    )}
                  </p>
                </div>

                 <div style={{ display: "block", width: "100%" }}>
                  <h3 className="purple">{t("Security")}</h3>
                  <p>
                    {t(
                      "By using a self-employment company, you're employed and often covered by necessary insurance that protects you in case of work-related injuries. The company also ensures that taxes and employer contributions are paid correctly."
                    )}
                  </p>
                </div>

                 <div style={{ display: "block", width: "100%" }}>
                  <h3 className="purple">{t("Financial Transparency")}</h3>
                  <p>
                    {t(
                      "Most self-employment companies clearly display all fees, allowing you to easily see how much of the invoice goes to taxes, fees, and your own earnings."
                    )}
                  </p>
                </div>
              </div>
            </div>
          </section>
          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h2 className="purple">
                    {t(
                      "What Will My Salary Be When I Invoice Without a Company?"
                    )}
                  </h2>
                  <p>
                    {t(
                      "When you invoice without a company through a self-employment company like Invozio, it's natural to wonder how much of the invoiced amount will end up in your pocket as net salary. Here, we’ll go over what factors affect your salary, what the fees look like, and what you can expect to receive after all fees and taxes have been deducted."
                    )}
                  </p>
                </div>
              </div>
            </div>
          </section>
          <section className="guide-section">
            <div className="container">
              <div className="purple row align-items-center">
                <div className="guide">
                  <h3 className="purple">
                    {t("How Your Salary is Calculated")}
                  </h3>
                  <p>
                    {t(
                      "Your salary when invoicing without a company is calculated after the self-employment company has made deductions for various costs. These deductions include the service fee, social security contributions, and taxes. Here is a step-by-step process for how your salary is calculated:"
                    )}
                  </p>
                </div>
                <div>
                  <h4 className="purple">
                    {t("Deduction for the Self-Employment Company’s Fee")}
                  </h4>
                  <p>
                    {t(
                      "Invozio charges a fee of 1.9% of the invoiced amount. This fee is often lower than other self-employment companies and is used to cover the costs of administration, invoicing, and services. The fee is deducted from the total invoiced amount."
                    )}
                  </p>
                </div>

                <div>
                  <h4 className="purple">
                    {t("Deduction for Social Security Contributions")}
                  </h4>
                  <p>
                    {t(
                      "After the service fee is deducted, the remaining amount is used to calculate social security contributions. Social security contributions in Sweden are around 31.42% of your gross salary and cover things like pensions and health insurance."
                    )}
                  </p>
                </div>

                <div>
                  <h4 className="purple">{t("Deduction for Income Tax")}</h4>
                  <p>
                    {t(
                      "Once social security contributions are deducted, the remaining amount is taxed based on your income tax rate according to your tax table. The tax rate depends on your income and may vary depending on your municipality, but it typically ranges between 30-33% for most people."
                    )}
                  </p>
                </div>
              </div>
            </div>
          </section>
          <section className="guide-section">
            <div className="container">
              <div className="row justify-center align-items-center">
                <div className="guide flex flex-col justify-center items-center">
                  <h3 className="purple">
                    {t("Try Calculating Your Gross and Net Salary")}
                  </h3>
                  <p>
                    {t(
                      "Below, you can easily calculate your salary based on the amount you invoice without a company, excluding VAT."
                    )}
                  </p>
                </div>
              </div>
            </div>
          </section>
          <OuterCalculator />

          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h4 className="purple">
                    {t("Factors That Affect Your Salary")}
                  </h4>
                  <p>
                    {t(
                      "There are certain factors that can affect how much you receive in your payout:"
                    )}
                  </p>
                  <p>
                    {t(
                      "Your municipal tax rate and any job tax deductions impact how much income tax is withheld. If you have multiple assignments or a high annual income, this can influence your final tax rate."
                    )}
                  </p>
                  <p>
                    {t(
                      "If you have expenses for materials, travel, or other work-related costs, you can deduct these by submitting receipts to Invozio. This reduces the taxable amount, thereby increasing your net salary."
                    )}
                  </p>
                </div>
              </div>
            </div>
          </section>
          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h3 className="purple">
                    {t("Why Choose Self-Employment Despite the Fees?")}
                  </h3>
                  <p>
                    {t(
                      "Even though deductions are made for fees, social security contributions, and taxes, many choose self-employment because of the simplicity and security it offers. You avoid managing bookkeeping and tax declarations, which can save a lot of time and energy, especially if you take on assignments sporadically. You also gain access to insurance and the security of being employed, which can be difficult to arrange on your own as a business owner."
                    )}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="guide-section">
            <div className="container">
              <div className="purple row align-items-center">
                <div className="guide">
                  <h2 className="purple">
                    {t("Invoicing Without a Company in Practice")}
                  </h2>
                  <p>
                    {t(
                      "Invozio offers a simple and efficient solution for those who want to invoice without a company. Their platform allows you to manage the entire invoicing process in just a few steps:"
                    )}
                  </p>
                </div>
                 <div style={{ display: "block", width: "100%" }}>
                  <h4 className="purple">{t("Register")}</h4>
                  <p>
                    {t(
                      "Create an account on Invozio’s platform, where you fill in your details and verify your identity."
                    )}
                  </p>
                </div>

                 <div style={{ display: "block", width: "100%" }}>
                  <h4 className="purple">{t("Create Invoice")}</h4>
                  <p>
                    {t(
                      "Once you have an assignment, you can enter the assignment information and customer details into the system. The invoice is then easily generated on Invozio’s platform."
                    )}
                  </p>
                </div>
                 <div style={{ display: "block", width: "100%" }}>
                  <h4 className="purple">{t("Invozio Sends the Invoice")}</h4>
                  <p>
                    {t(
                      "Invozio reviews and sends the invoice directly to the client, saving you time and ensuring that all the details are correct."
                    )}
                  </p>
                </div>
              </div>
            </div>
          </section>
          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h4 className="purple">
                    {t("Get Paid for the Sent Invoice")}
                  </h4>
                  <p>
                    {t(
                      "Once the customer has paid the invoice, your salary is paid out after all taxes and fees have been deducted. Payments are usually processed within 1–2 business days after the customer’s payment."
                    )}
                  </p>
                  <blockquote className="blockquote_txt">
                    {t("Didrik Selder, CEO at Invozio, says:")}
                    {t(
                      " “Our ambition is to make it as simple as possible for freelancers and consultants to invoice without having to start their own company. With Invozio, you can focus on what you're best at, while we take care of the rest.”"
                    )}
                  </blockquote>
                </div>
              </div>
            </div>
          </section>
          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h2 className="purple">
                    {t("Costs and Fees for Invoicing with Invozio")}
                  </h2>
                  <p>
                    {t("Invozio offers a competitive fee of")}
                    <strong>{t(" only 1.9% of the invoiced amount, ")}</strong>
                    {t(
                      "making it one of the lowest rates on the market. This means you get to keep a larger share of your earnings compared to many other self-employment companies. Other options on the market include, for example:"
                    )}
                  </p>
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th className="invoice_tabeletext">
                          {t("Self-Employment Company")}
                        </th>
                        <th className="invoice_tabeletext">{t("Fee")}</th>
                        <th className="invoice_tabeletext">
                          {t("Insurance ")}
                        </th>
                        <th className="invoice_tabeletext">
                          {t("Customer Support")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="invoice_tabeletext">{t("Invozio")}</td>
                        <td className="invoice_tabeletext">{t("1.9 %")}</td>
                        <td className="invoice_tabeletext">{t("Yes")}</td>
                        <td className="invoice_tabeletext">{t("High")}</td>
                      </tr>
                      <tr>
                        <td className="invoice_tabeletext">
                          {t("Frilans Finans")}
                        </td>
                        <td className="invoice_tabeletext">{t("6 %")}</td>
                        <td className="invoice_tabeletext">{t("Yes")}</td>
                        <td className="invoice_tabeletext">{t("Medium")}</td>
                      </tr>
                      <tr>
                        <td className="invoice_tabeletext">
                          {t("Cool Company")}
                        </td>
                        <td className="invoice_tabeletext">{t("5,98 %")}</td>
                        <td className="invoice_tabeletext">{t("Yes")}</td>
                        <td className="invoice_tabeletext">{t("Medium")}</td>
                      </tr>
                    </tbody>
                  </Table>
                  <p>
                    {t(
                      "The low fee makes Invozio a cost-effective option, especially for those planning to invoice large amounts or for ongoing invoicing needs."
                    )}
                  </p>
                  <Link
                    style={{
                      fontWeight: "600",
                      textDecoration: "underline",
                    }}
                    to={
                      language === LOCALES.EN_US
                        ? `/recension-av-faktura-utan-foretag-frilansfinans-coolcompany-gigger?lang=${LOCALES.EN_US}`
                        : `/recension-av-faktura-utan-foretag-frilansfinans-coolcompany-gigger?lang=${LOCALES.SV_SE}`
                    }
                  >
                    {" "}
                    <u>
                      {t(
                        "Read more about our comparison with other self-employment companies"
                      )}
                    </u>
                  </Link>{" "}
                </div>
              </div>
            </div>
          </section>
          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h2 className="purple">
                    {t("Insurance and Security with Invozio")}
                  </h2>
                  <p>
                    {t(
                      "When you invoice through Invozio, you are automatically covered by liability insurance and accident insurance. This is especially important for freelancers and consultants working in uncertain environments or undertaking risky assignments."
                    )}
                    <Link
                      style={{
                        fontWeight: "600",
                        textDecoration: "underline",
                      }}
                      to={
                        user
                          ? language === LOCALES.EN_US
                            ? `/dashboard/insurances?lang=${LOCALES.EN_US}`
                            : `/instrumentbrada/forsakring?lang=${LOCALES.SV_SE}`
                          : language === LOCALES.EN_US
                          ? `/insurance?lang=${LOCALES.EN_US}`
                          : `/forsakring?lang=${LOCALES.SV_SE}`
                      }
                    >
                      <u>{t("The insurances")}</u>
                    </Link>{" "}

                    {t(
                      "provides additional protection in case something unexpected happens during your work, offering security for both you and your clients."
                    )}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h2 className="purple">{t("Get Started with Invozio")}</h2>
                  <p>
                    {t(
                      "Invoicing without having your own company via Invozio is simple and requires just a few steps. Here’s a quick guide to getting started:"
                    )}
                  </p>
                </div>
                 <div style={{ display: "block", width: "100%" }}>
                  <h4 className="purple">{t("Register Yourself")}</h4>
                  <p>
                    {t(
                      "Create an account on Invozio’s website and fill in your personal details."
                    )}
                  </p>
                </div>

                <div style={{ display: "block", width: "100%" }}>
                  <h4 className="purple">{t("Add Your Bank Details")}</h4>
                  <p>
                    {t(
                      "To receive your payments, you’ll need to enter your bank information."
                    )}
                  </p>
                </div>

                <div style={{ display: "block", width: "100%" }}>
                  <h4 className="purple">{t("Create Your First Invoice")}</h4>
                  <p>
                    {t(
                      "Fill in the details of your project and client, and Invozio will send the invoice on your behalf."
                    )}
                  </p>
                </div>
                <div style={{ display: "block", width: "100%" }}>
                  <h4 className="purple">{t("Receive Your Payment")}</h4>
                  <p>
                    {t(
                      "Once the invoice is paid, you’ll receive your salary within a couple of business days, fully taxed and processed."
                    )}
                  </p>
                  <blockquote className="blockquote_txt">
                    {t(
                      "“Our strategy at Invozio is to combine full functionality with our low 1.9% fee to offer cost-effective solutions for freelancers and consultants”, says Didrik Selder, CEO at Invozio."
                    )}
                  </blockquote>
                </div>
              </div>
            </div>
          </section>
          <section className="guide-section">
            <div className="container">
              <div className="purple row align-items-center">
                <div className="guide">
                  <h2 className="purple">{t("Frequently Asked Questions")}</h2>
                </div>
                <div style={{ display: "block", width: "100%" }}>
                  <h3 className="purple">
                    {t(
                      "Can I invoice both companies and private individuals through Invozio?"
                    )}
                  </h3>
                  <p>
                    {t(
                      "Yes, with Invozio, you can invoice both companies and private individuals, making it easy to handle various types of projects."
                    )}
                  </p>
                </div>

                <div style={{ display: "block", width: "100%" }}>
                  <h3 className="purple">
                    {t("How long does it take to get paid?")}
                  </h3>
                  <p>
                    {t(
                      "Once your client has paid the invoice, it typically takes 1–2 business days for Invozio to transfer your salary."
                    )}
                  </p>
                </div>

                <div style={{ display: "block", width: "100%" }}>
                  <h3 className="purple">
                    {t("Can I deduct expenses related to the project?")}
                  </h3>
                  <p>
                    {t(
                      "Yes, if you have expenses related to a project, you can submit receipts to Invozio, which will handle these deductions for you."
                    )}
                  </p>
                </div>

                <div style={{ display: "block", width: "100%" }}>
                  <h3 className="purple">
                    {t("Is there a limit to how much I can invoice?")}
                  </h3>
                  <p>
                    {t(
                      "No, Invozio does not impose an upper limit on invoice amounts, so you can bill large sums if your project requires it."
                    )}
                  </p>
                </div>

                <div style={{ display: "block", width: "100%" }}>
                  <h3 className="purple">
                    {t(
                      "Do I need to declare the income I invoice through Invozio?"
                    )}
                  </h3>
                  <p>
                    {t(
                      "Since Invozio handles tax payments for your invoiced projects, you do not need to manage tax declarations yourself for these earnings."
                    )}
                  </p>
                </div>
              </div>
            </div>
          </section>
          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h2 className="purple">
                    {t("Why Invoicing Without a Company is the Right Choice")}
                  </h2>
                  <p>
                    {t(
                      "Invoicing without a company through a service like Invozio is an excellent solution for those who want to work flexibly and avoid the administrative burdens of running their own business. Additionally, it offers a lower cost for consultants or freelancers invoicing up to 150,000 SEK per month."
                    )}
                  </p>
                </div>
              </div>
            </div>
          </section>
          <section className="guide-section">
            <div className="container">
              <div className="row align-items-center">
                <div className="guide">
                  <h3 className="purple">
                    {t("Security for You as a Self-Employed Worker")}
                  </h3>
                  <p>
                    {t(
                      "It provides you with the security of being employed while enjoying the freedom to work whenever and however you choose. With low fees and built-in insurance, Invozio offers a complete solution for freelancers and consultants. If you want to get started, visit Invozio's"
                    )}
                    <Link
                      style={{
                        fontWeight: "600",
                        textDecoration: "underline",
                      }}
                      to={
                        language === LOCALES.EN_US
                          ? `/emailregister?lang=${LOCALES.EN_US}`
                          : `/e-postregister?lang=${LOCALES.SV_SE}`
                      }
                    >
                      {" "}
                      <u>{t("registration page")}</u>
                    </Link>{" "}
                    {t(
                      "and easily create an account to start invoicing today."
                    )}
                  </p>
                </div>
              </div>
              {!user && (
                <Button
                  className="self_emp create-account filled home_create_account"
                  onClick={() => {
                    navigate(
                      language === LOCALES.EN_US
                        ? `/emailregister?lang=${LOCALES.EN_US}`
                        : `/e-postregister?lang=${LOCALES.SV_SE}`
                    );
                    sessionStorage.setItem("accountType", 1);
                  }}
                >
                  {t("Register and Start Invoicing Without a Company Today")}
                </Button>
              )}
            </div>
          </section>
        </div>
      </Container>
      <Footer dashboardFooter={dashboardFooter} />
    </div>
  );
};
